import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';

// Only the homepage (LCP route), NotFound (tiny fallback), and the
// always-visible shell chrome are imported eagerly. Every other route is
// code-split via React.lazy so the initial JS payload for / stays small
// and users only download the code for pages they actually visit.
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import StickyCallButton from "./components/StickyCallButton";
import RedirectHandler from "./components/RedirectHandler";
import ScrollToTop from "./components/ScrollToTop";
import { HolidayProvider } from "./contexts/HolidayContext";

// Static, non-city, non-blog public pages.
const About = lazy(() => import("./pages/About"));
const Keith = lazy(() => import("./pages/Keith"));
const Services = lazy(() => import("./pages/Services"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Cities = lazy(() => import("./pages/Cities"));
const NowHiring = lazy(() => import("./pages/NowHiring"));
const Affiliations = lazy(() => import("./pages/Affiliations"));
const RepairsAndMaintenance = lazy(() => import("./pages/RepairsAndMaintenance"));
const ElectricalServices = lazy(() => import("./pages/ElectricalServices"));

// Service pages.
const ResidentialServices = lazy(() => import("./pages/services/ResidentialServices"));
const CommercialServices = lazy(() => import("./pages/services/CommercialServices"));
const NewConstruction = lazy(() => import("./pages/services/NewConstruction"));
const Remodels = lazy(() => import("./pages/services/Remodels"));
const PanelUpgrades = lazy(() => import("./pages/services/PanelUpgrades"));
const GeneratorInstallation = lazy(() => import("./pages/services/GeneratorInstallation"));
const EVChargingStations = lazy(() => import("./pages/services/EVChargingStations"));
const DesignerLighting = lazy(() => import("./pages/services/DesignerLighting"));
const SafetyInspections = lazy(() => import("./pages/services/SafetyInspections"));
const NewHome = lazy(() => import("./pages/services/NewHome"));
const WireCommercialBuildOut = lazy(() => import("./pages/services/WireCommercialBuildOut"));
const ElectricalPanelUpgrade = lazy(() => import("./pages/services/ElectricalPanelUpgrade"));
const ApplianceInstallation = lazy(() => import("./pages/services/ApplianceInstallation"));

// County pages.
const CountiesIndex = lazy(() => import("./pages/counties/CountiesIndex"));
const CountyPage = lazy(() => import("./pages/counties/CountyPage"));

// Blog pages.
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const EVChargerCostVolusia2026 = lazy(() => import("./pages/blog/EVChargerCostVolusia2026"));
const GeneratorCostVolusia2026 = lazy(() => import("./pages/blog/GeneratorCostVolusia2026"));

// Free planning tools. Each is a self-contained multi-step estimator that
// ends with a Formcarry lead form — the primary lead-gen path for visitors
// who aren't ready to call directly.
const ToolsHub = lazy(() => import("./pages/tools/ToolsHub"));
const EVChargerEstimator = lazy(() => import("./pages/tools/EVChargerEstimator"));
const PanelUpgradeChecker = lazy(() => import("./pages/tools/PanelUpgradeChecker"));
const GeneratorSizer = lazy(() => import("./pages/tools/GeneratorSizer"));
const SafetyTriageQuiz = lazy(() => import("./pages/tools/SafetyTriageQuiz"));
const QuoteBuilder = lazy(() => import("./pages/tools/QuoteBuilder"));

// City pages — each split into its own chunk so a visit to /locations/orlando
// does not pull in markup for Deltona, Sanford, Port Orange, etc.
const DeLeonSprings = lazy(() => import("./pages/cities/DeLeonSprings"));
const Maitland = lazy(() => import("./pages/cities/Maitland"));
const Orlando = lazy(() => import("./pages/cities/Orlando"));
const DaytonaBeach = lazy(() => import("./pages/cities/DaytonaBeach"));
const Deltona = lazy(() => import("./pages/cities/Deltona"));
const Sanford = lazy(() => import("./pages/cities/Sanford"));
const DeLand = lazy(() => import("./pages/cities/DeLand"));
const NewSmyrnaBeach = lazy(() => import("./pages/cities/NewSmyrnaBeach"));
const PortOrange = lazy(() => import("./pages/cities/PortOrange"));
const OrmondBeach = lazy(() => import("./pages/cities/OrmondBeach"));
const WinterPark = lazy(() => import("./pages/cities/WinterPark"));
const LakeHelen = lazy(() => import("./pages/cities/LakeHelen"));
const LakeMary = lazy(() => import("./pages/cities/LakeMary"));
const WinterSprings = lazy(() => import("./pages/cities/WinterSprings"));
const Casselberry = lazy(() => import("./pages/cities/Casselberry"));
const Oviedo = lazy(() => import("./pages/cities/Oviedo"));
const Longwood = lazy(() => import("./pages/cities/Longwood"));
const AltamonteSprings = lazy(() => import("./pages/cities/AltamonteSprings"));
const DeBary = lazy(() => import("./pages/cities/DeBary"));
const Edgewater = lazy(() => import("./pages/cities/Edgewater"));
const HollyHill = lazy(() => import("./pages/cities/HollyHill"));
const SouthDaytona = lazy(() => import("./pages/cities/SouthDaytona"));
const Pierson = lazy(() => import("./pages/cities/Pierson"));
const WestDeLand = lazy(() => import("./pages/cities/WestDeLand"));
const NorthDeLand = lazy(() => import("./pages/cities/NorthDeLand"));
const DeLandSouthwest = lazy(() => import("./pages/cities/DeLandSouthwest"));
const LakeMackForestHills = lazy(() => import("./pages/cities/LakeMackForestHills"));
const Astor = lazy(() => import("./pages/cities/Astor"));
const TheVillages = lazy(() => import("./pages/cities/TheVillages"));
const WinterGarden = lazy(() => import("./pages/cities/WinterGarden"));
const Apopka = lazy(() => import("./pages/cities/Apopka"));
const Ocoee = lazy(() => import("./pages/cities/Ocoee"));
const Isleworth = lazy(() => import("./pages/cities/Isleworth"));
const OrangeCity = lazy(() => import("./pages/cities/OrangeCity"));
const OakHill = lazy(() => import("./pages/cities/OakHill"));

// Admin pages — already lazy.
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminTestimonials = lazy(() => import("./pages/admin/Testimonials"));
const AdminFAQs = lazy(() => import("./pages/admin/FAQs"));
const AdminLocations = lazy(() => import("./pages/admin/Locations"));
const AdminMedia = lazy(() => import("./pages/admin/Media"));
const MigrationTools = lazy(() => import("./pages/admin/MigrationTools"));
const AdminImageMigration = lazy(() => import("./pages/admin/ImageMigration"));
const AdminHolidaySettings = lazy(() => import("./pages/admin/HolidaySettings"));

const RouteFallback = () => (
  <div
    className="flex items-center justify-center min-h-[60vh] text-navy/60"
    role="status"
    aria-live="polite"
  >
    Loading…
  </div>
);

const AdminFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh] text-navy/60">
    Loading…
  </div>
);

const queryClient = new QueryClient();

function WebsiteChrome({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return pathname.replace(/\/$/, '') === '/keith' ? null : <>{children}</>;
}

function WebsiteLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isCard = pathname.replace(/\/$/, '') === '/keith';
  return <div className={`min-h-screen flex flex-col${isCard ? '' : ' website-layout'}`}>{children}</div>;
}

function WebsiteMain({ children }: { children: React.ReactNode }) {
  return <main id="main-content" tabIndex={-1} className="flex-grow">{children}</main>;
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <HolidayProvider>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <RedirectHandler />
              <ScrollToTop />
              <WebsiteLayout>
              <a
                href="#main-content"
                className="
                  sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100]
                  focus:bg-orange focus:text-white focus:font-semibold
                  focus:px-4 focus:py-2 focus:rounded focus:shadow-lg
                  focus:outline-none focus:ring-2 focus:ring-white
                "
              >
                Skip to main content
              </a>
              <WebsiteChrome><Navbar /></WebsiteChrome>
              <WebsiteMain>
                {/* One shared Suspense boundary wraps every lazy route. The
                    homepage is eagerly imported so LCP does not wait on a
                    chunk fetch; everything else streams in behind the
                    fallback. */}
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/keith" element={<Keith />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/thank-you" element={<ThankYou />} />
                  <Route path="/locations" element={<Cities />} />
                  <Route path="/now-hiring" element={<NowHiring />} />
                  <Route path="/affiliations" element={<Affiliations />} />
                  <Route path="/repairs-and-maintenance" element={<RepairsAndMaintenance />} />
                  <Route path="/electrical-services" element={<ElectricalServices />} />
                  <Route path="/electrical-panel-upgrade" element={<ElectricalPanelUpgrade />} />
                  <Route path="/installation-of-appliance" element={<ApplianceInstallation />} />
                  <Route path="/locations/de-leon-springs" element={<DeLeonSprings />} />
                  <Route path="/locations/maitland" element={<Maitland />} />

                  {/* County Routes */}
                  <Route path="/locations/counties" element={<CountiesIndex />} />
                  <Route path="/locations/counties/:countySlug" element={<CountyPage />} />

                  {/* Blog Routes */}
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/ev-charger-installation-cost-volusia-county-2026" element={<EVChargerCostVolusia2026 />} />
                  <Route path="/blog/whole-house-generator-cost-volusia-county-2026" element={<GeneratorCostVolusia2026 />} />

                  {/* Free planning tools (lead-gen) */}
                  <Route path="/tools" element={<ToolsHub />} />
                  <Route path="/tools/ev-charger-estimator" element={<EVChargerEstimator />} />
                  <Route path="/tools/panel-upgrade-checker" element={<PanelUpgradeChecker />} />
                  <Route path="/tools/generator-sizer" element={<GeneratorSizer />} />
                  <Route path="/tools/safety-triage" element={<SafetyTriageQuiz />} />
                  <Route path="/tools/quote-builder" element={<QuoteBuilder />} />

                  {/* Service Routes */}
                  <Route path="/services/residential" element={<ResidentialServices />} />
                  <Route path="/services/commercial" element={<CommercialServices />} />
                  <Route path="/services/new-construction" element={<NewConstruction />} />
                  <Route path="/services/remodels" element={<Remodels />} />
                  <Route path="/services/panel-upgrades" element={<PanelUpgrades />} />
                  <Route path="/services/generators" element={<GeneratorInstallation />} />
                  <Route path="/services/generator-installation" element={<GeneratorInstallation />} />
                  <Route path="/services/ev-charging" element={<EVChargingStations />} />
                  <Route path="/designer-lighting" element={<DesignerLighting />} />
                  <Route path="/safety-inspections" element={<SafetyInspections />} />
                  <Route path="/generator-installations" element={<GeneratorInstallation />} />
                  <Route path="/new-home" element={<NewHome />} />
                  <Route path="/wire-commercial-build-out" element={<WireCommercialBuildOut />} />

                  {/* City Routes */}
                  <Route path="/locations/orlando" element={<Orlando />} />
                  <Route path="/locations/daytona-beach" element={<DaytonaBeach />} />
                  <Route path="/locations/deltona" element={<Deltona />} />
                  <Route path="/locations/sanford" element={<Sanford />} />
                  <Route path="/locations/deland" element={<DeLand />} />
                  <Route path="/locations/new-smyrna-beach" element={<NewSmyrnaBeach />} />
                  <Route path="/locations/port-orange" element={<PortOrange />} />
                  <Route path="/locations/ormond-beach" element={<OrmondBeach />} />
                  <Route path="/locations/winter-park" element={<WinterPark />} />
                  <Route path="/locations/lake-helen" element={<LakeHelen />} />
                  <Route path="/locations/lake-mary" element={<LakeMary />} />
                  <Route path="/locations/winter-springs" element={<WinterSprings />} />
                  <Route path="/locations/casselberry" element={<Casselberry />} />
                  <Route path="/locations/oviedo" element={<Oviedo />} />
                  <Route path="/locations/longwood" element={<Longwood />} />
                  <Route path="/locations/altamonte-springs" element={<AltamonteSprings />} />
                  <Route path="/locations/debary" element={<DeBary />} />
                  <Route path="/locations/edgewater" element={<Edgewater />} />
                  <Route path="/locations/holly-hill" element={<HollyHill />} />
                  <Route path="/locations/south-daytona" element={<SouthDaytona />} />
                  <Route path="/locations/deleon-springs" element={<DeLeonSprings />} />
                  <Route path="/locations/pierson" element={<Pierson />} />
                  <Route path="/locations/west-deland" element={<WestDeLand />} />
                  <Route path="/locations/north-deland" element={<NorthDeLand />} />
                  <Route path="/locations/deland-southwest" element={<DeLandSouthwest />} />
                  <Route path="/locations/lake-mack-forest-hills" element={<LakeMackForestHills />} />
                  <Route path="/locations/astor" element={<Astor />} />
                  <Route path="/locations/the-villages" element={<TheVillages />} />
                  <Route path="/locations/winter-garden" element={<WinterGarden />} />
                  <Route path="/locations/apopka" element={<Apopka />} />
                  <Route path="/locations/ocoee" element={<Ocoee />} />
                  <Route path="/locations/isleworth" element={<Isleworth />} />
                  <Route path="/locations/orange-city" element={<OrangeCity />} />
                  <Route path="/locations/oak-hill" element={<OakHill />} />

                  {/* Admin Routes — wrapped in Suspense for lazy chunks. */}
                  <Route
                    path="/admin/login"
                    element={
                      <Suspense fallback={<AdminFallback />}>
                        <AdminLogin />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <Suspense fallback={<AdminFallback />}>
                        <AdminLayout />
                      </Suspense>
                    }
                  >
                    <Route index element={<Suspense fallback={<AdminFallback />}><AdminDashboard /></Suspense>} />
                    <Route path="dashboard" element={<Suspense fallback={<AdminFallback />}><AdminDashboard /></Suspense>} />
                    <Route path="testimonials" element={<Suspense fallback={<AdminFallback />}><AdminTestimonials /></Suspense>} />
                    <Route path="faqs" element={<Suspense fallback={<AdminFallback />}><AdminFAQs /></Suspense>} />
                    <Route path="locations" element={<Suspense fallback={<AdminFallback />}><AdminLocations /></Suspense>} />
                    <Route path="media" element={<Suspense fallback={<AdminFallback />}><AdminMedia /></Suspense>} />
                    <Route path="migration" element={<Suspense fallback={<AdminFallback />}><MigrationTools /></Suspense>} />
                    <Route path="image-migration" element={<Suspense fallback={<AdminFallback />}><AdminImageMigration /></Suspense>} />
                    <Route path="holidays" element={<Suspense fallback={<AdminFallback />}><AdminHolidaySettings /></Suspense>} />
                  </Route>

                  {/* Legacy /cities/* redirected to /locations/* by vercel.json
                      and public/_redirects at the edge — no SPA routes kept here
                      to avoid serving duplicate content if the edge redirect is
                      ever bypassed (preview deploys, etc.). */}

                  <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </WebsiteMain>
              <WebsiteChrome><Footer /><StickyCallButton /></WebsiteChrome>
            </WebsiteLayout>
          </BrowserRouter>
        </TooltipProvider>
      </HolidayProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
}

export default App;
